/* @img-migrated */
import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { ImagePlus, X, Loader2, RefreshCw, AlignVerticalJustifyCenter, AlignStartVertical, AlignEndVertical } from 'lucide-react';
import { toast } from 'sonner';

import Img from './Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RAW_BYTES = 8 * 1024 * 1024;

/**
 * Client-side image compressor:
 *  - Resize to `maxEdge` on the longest side.
 *  - Re-encode as JPEG `quality`.
 *  - Skip if already small (<350 KB).
 */
async function compressImage(file, { maxEdge = 1920, quality = 0.85 } = {}) {
  if (!file.type.startsWith('image/')) return file;
  if (file.size < 350 * 1024) return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  const src = bitmap || (await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = URL.createObjectURL(file);
  }));
  const w = src.width || src.naturalWidth;
  const h = src.height || src.naturalHeight;
  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const outW = Math.round(w * scale);
  const outH = Math.round(h * scale);
  const canvas = document.createElement('canvas');
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext('2d');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(src, 0, 0, outW, outH);
  const blob = await new Promise((resolve) => canvas.toBlob((b) => resolve(b || file), 'image/jpeg', quality));
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], (file.name || 'image').replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
}

const POSITIONS = [
  { id: 'top', label: 'Top', icon: AlignStartVertical, objectPosition: 'center top' },
  { id: 'center', label: 'Center', icon: AlignVerticalJustifyCenter, objectPosition: 'center center' },
  { id: 'bottom', label: 'Bottom', icon: AlignEndVertical, objectPosition: 'center bottom' },
];

/**
 * BlogImageUploader — premium single-image uploader with drag-drop, preview,
 * position selector (top/center/bottom), and replace/remove actions.
 *
 * Props:
 *   value       - current image URL (string | null)
 *   position    - 'top' | 'center' | 'bottom' (default 'center')
 *   onChange    - (url, position) => void
 *   aspect      - 'video' (16:9, default) | 'square' | 'wide' (21:9)
 *   endpoint    - optional, defaults to /api/blog/upload-image
 */
export const BlogImageUploader = ({
  value,
  position = 'center',
  onChange,
  aspect = 'video',
  endpoint = `${API}/blog/upload-image`,
  label = 'Featured Image',
  hint = 'Recommended 16:9 · 1600×900 or larger · PNG / JPG / WebP · 8 MB max',
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const aspectClass = aspect === 'square' ? 'aspect-square' : aspect === 'wide' ? 'aspect-[21/9]' : 'aspect-video';

  const handleFiles = useCallback(async (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    if (file.size > MAX_RAW_BYTES) {
      toast.error('Image exceeds 8 MB limit');
      return;
    }
    setUploading(true);
    setProgress(5);
    try {
      const compressed = await compressImage(file);
      setProgress(25);
      const fd = new FormData();
      fd.append('file', compressed);
      const res = await axios.post(endpoint, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setProgress(25 + Math.round((e.loaded / e.total) * 70));
        },
      });
      const url = res.data?.image_url;
      if (!url) throw new Error('Upload returned no URL');
      setProgress(100);
      onChange?.(url, position);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  }, [endpoint, onChange, position]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
  };

  const clearImage = () => {
    if (uploading) return;
    onChange?.('', position);
  };

  const setPosition = (posId) => {
    onChange?.(value || '', posId);
  };

  return (
    <div className="space-y-2" data-testid="blog-image-uploader">
      <div className="flex items-baseline justify-between">
        <label className="block text-[12px] font-semibold text-slate-700">{label}</label>
        {value && (
          <button
            type="button"
            onClick={clearImage}
            className="text-[11px] font-medium text-rose-600 hover:text-rose-700 inline-flex items-center gap-1"
            data-testid="blog-image-remove"
          >
            <X className="w-3 h-3" />Remove
          </button>
        )}
      </div>

      {/* ----- Dropzone / Preview ----- */}
      {!value ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={`w-full ${aspectClass} rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center gap-2 ${
            dragOver ? 'border-[#002249] bg-[#002249]/5' : 'border-slate-300 hover:border-slate-400 bg-slate-50/60'
          }`}
          data-testid="blog-image-dropzone"
          disabled={uploading}
        >
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 text-[#002249] animate-spin" />
              <span className="text-[13px] font-semibold text-[#002249]">Uploading… {progress}%</span>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm">
                <ImagePlus className="w-6 h-6 text-[#002249]" />
              </div>
              <div className="text-center px-4">
                <p className="text-[13.5px] font-semibold text-[#002249]">
                  Click to upload <span className="text-slate-400 font-normal">or drag & drop</span>
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>
              </div>
            </>
          )}
        </button>
      ) : (
        <div className="relative group rounded-xl overflow-hidden border border-slate-200 shadow-sm bg-slate-900">
          <div className={`${aspectClass} w-full bg-slate-100`}>
            <Img variant="generic"
              src={resolveImg(value)}
              alt="Featured"
              className="w-full h-full object-cover"
              style={{ objectPosition: POSITIONS.find((p) => p.id === position)?.objectPosition || 'center center' }}
              data-testid="blog-image-preview"
            />
          </div>
          {/* Hover actions */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="px-3 py-2 rounded-full bg-white text-[#002249] text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-lg hover:bg-slate-50"
                data-testid="blog-image-replace"
                disabled={uploading}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                Replace
              </button>
              <button
                type="button"
                onClick={clearImage}
                className="px-3 py-2 rounded-full bg-rose-600 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 shadow-lg hover:bg-rose-700"
                data-testid="blog-image-remove-hover"
              >
                <X className="w-3.5 h-3.5" />Remove
              </button>
            </div>
          </div>

          {uploading && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-800/40">
              <div className="h-full bg-[#E63946] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      )}

      {/* ----- Position selector (only when image present) ----- */}
      {value && (
        <div className="flex items-center gap-2 pt-1" data-testid="blog-image-position">
          <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Focal point</span>
          <div className="inline-flex items-center gap-1 p-0.5 bg-slate-100 rounded-lg">
            {POSITIONS.map((p) => {
              const Icon = p.icon;
              const active = p.id === position;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setPosition(p.id)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold inline-flex items-center gap-1 transition-all ${
                    active ? 'bg-white text-[#002249] shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}
                  data-testid={`blog-image-pos-${p.id}`}
                >
                  <Icon className="w-3 h-3" />
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Hidden native file input — supports mobile camera via `capture` */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="blog-image-file-input"
      />
    </div>
  );
};

export default BlogImageUploader;
